import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FileText, Sparkles, Calendar, Clock } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const activityIcons = {
  script_created: FileText,
  ideas_generated: Sparkles,
  post_scheduled: Calendar
};

const activityColors = {
  script_created: 'bg-[#82A08B]/20 text-[#2C5F44]',
  ideas_generated: 'bg-[#D98A6C]/20 text-[#D98A6C]',
  post_scheduled: 'bg-blue-100 text-blue-600'
};

export default function ActivityFeed({ doctorId, limit = 5 }) {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchActivity();
  }, [doctorId]);

  const fetchActivity = async () => {
    try {
      const response = await axios.get(`${API}/activity/${doctorId}?limit=${limit}`);
      setActivities(response.data.activities || []);
    } catch (error) {
      console.error('Error fetching activity:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatTimeAgo = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const seconds = Math.floor((now - date) / 1000);

    if (seconds < 60) return 'Just now';
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
    if (seconds < 604800) return `${Math.floor(seconds / 86400)}d ago`;
    return date.toLocaleDateString();
  };

  if (loading) {
    return (
      <div className="space-y-3">
        {[1, 2, 3].map((i) => (
          <div key={i} className="flex items-start gap-3 p-3 bg-gray-100 rounded-xl animate-pulse">
            <div className="w-10 h-10 bg-gray-200 rounded-full" />
            <div className="flex-1 space-y-2">
              <div className="h-4 bg-gray-200 rounded w-3/4" />
              <div className="h-3 bg-gray-200 rounded w-1/2" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (activities.length === 0) {
    return (
      <div className="text-center py-8">
        <Clock className="w-12 h-12 text-[#5C7063] mx-auto mb-3 opacity-50" strokeWidth={1.5} />
        <p className="text-sm text-[#5C7063]">No recent activity</p>
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="activity-feed">
      {activities.map((activity, index) => {
        const Icon = activityIcons[activity.type] || FileText;
        const colorClass = activityColors[activity.type] || 'bg-gray-100 text-gray-600';

        return (
          <motion.div
            key={activity.id || index}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.3, delay: index * 0.05 }}
            className="flex items-start gap-3 p-3 bg-white rounded-xl border border-[#2C5F44]/10 hover:shadow-sm transition-shadow"
            data-testid={`activity-item-${index}`}
          >
            <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${colorClass}`}>
              <Icon className="w-5 h-5" strokeWidth={1.5} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm text-[#1C2A21] font-medium truncate">
                {activity.title}
              </p>
              <p className="text-xs text-[#5C7063] mt-0.5">
                {formatTimeAgo(activity.timestamp)}
              </p>
            </div>
          </motion.div>
        );
      })}
    </div>
  );
}
